import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'
import { DocStep } from '../shared/DocStep'

const Embeds = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Guild Embeds let you display your Guild's Events on any external
            website. Embeds are lightweight, self-contained widgets that show your
            upcoming Events in a clean card layout. This is ideal for organizations
            that have their own website and want to showcase their community Events
            without sending visitors away from their site.
          </Trans>
        </Paragraph>
        <Paragraph size='$5'>
          <Trans>
            Embeds update automatically — when you create a new{' '}
            <Link href='/docs/events/overview'>Event</Link> or update an existing
            one, the embed reflects the changes without needing to touch the code on
            your website.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/guilds/guild-settings-embeds.png'
          alt={t`Guild Embeds settings page showing embed preview and HTML code snippet for embedding Events on external websites`}
          caption={t`The Embeds settings page with a live preview and copyable embed code`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='getting-embed-code' title={t`Getting the Embed Code`}>
          <Paragraph size='$5'>
            <Trans>
              Only <Link href='/docs/guilds/members'>Organizers</Link> can access
              embed codes for their Guild.
            </Trans>
          </Paragraph>
          <DocStep
            number={1}
            title={t`Open Guild Settings`}
            description={t`Navigate to your Guild's profile page and click the settings gear icon.`}
          />
          <DocStep
            number={2}
            title={t`Find the Embed section`}
            description={t`In Settings, locate the Embed section where you can view and copy your embed code.`}
          />
          <DocStep
            number={3}
            title={t`Copy the embed code`}
            description={t`Copy the provided HTML snippet. This includes a small script tag and a container element.`}
          />
          <DocStep
            number={4}
            title={t`Paste into your website`}
            description={t`Add the embed code to any HTML page where you want the Events widget to appear. The widget will load and render your Guild's upcoming Events automatically.`}
          />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='embed-features' title={t`What the Embed Shows`}>
          <Paragraph size='$5'>
            <Trans>
              The Guild Embed displays your upcoming Events as cards, each showing the
              Event name, date and time, venue, and a link to the full Event page on
              Guild where visitors can RSVP or purchase tickets.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              Embeds are styled independently of your website's CSS. Guild uses Shadow
              DOM isolation to ensure the embed looks consistent regardless of the
              styles on the host page. This means the embed won't be affected by your
              site's fonts, colors, or layout rules — and it won't affect them either.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='embed-customization' title={t`Customization`}>
          <Paragraph size='$5'>
            <Trans>
              The embed is designed to blend naturally into most websites. The widget
              adapts to the width of its container, so it works in sidebars, full-width
              sections, or any other layout. You can control the size by adjusting the
              width of the container element in your page's HTML or CSS.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              If you need more control over how Events are displayed on your website,
              consider using{' '}
              <Link href='/docs/guilds/calendar-feeds'>Calendar Feeds</Link> to pull
              Event data into your own custom-built interface.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Embeds
